import { Link } from 'react-router-dom'
import { api } from '../api/client'
import type { Alert, GlobalStatus, TargetStatus } from '../api/types'
import { Empty, ErrorBanner, Loading, StatTile } from '../components/Common'
import { StatusChip } from '../components/StatusChip'
import { usePolling } from '../hooks/usePolling'
import { STATUS_FILL, STATUS_GLYPH, STATUS_LABEL, timeAgo } from '../theme/charts'

/**
 * The default view: is everything alright, and if not, what.
 *
 * <p>Ordered so the answer arrives before the detail — the ecosystem verdict
 * first, then anything currently firing, then the per-target grid. Someone
 * opening this at 3am should not have to read a chart to find out whether they
 * can go back to bed.
 */
export function Overview() {
  const status = usePolling<GlobalStatus>(() => api.status(), 15_000)
  const alerts = usePolling<Alert[]>(() => api.alerts(), 20_000)

  if (status.loading && !status.data) return <Loading what="status" />

  const data = status.data
  const firing = (alerts.data ?? []).filter((a) => a.state === 'firing')

  return (
    <div className="stack">
      <ErrorBanner error={status.error} />

      {data && <GlobalHero status={data} />}

      <div className="grid grid-4">
        <StatTile label="Healthy" value={data?.okCount ?? 0} tone="var(--status-ok)" />
        <StatTile label="Warning" value={data?.warningCount ?? 0} tone="var(--status-warning)" />
        <StatTile label="Critical" value={data?.criticalCount ?? 0} tone="var(--status-critical)" />
        <StatTile label="Unknown" value={data?.unknownCount ?? 0} tone="var(--status-unknown)" />
      </div>

      {firing.length > 0 && (
        <div className="card">
          <div className="card-header">
            <h3 className="card-title">Firing alerts</h3>
            <Link className="card-meta" to="/alerts">all alerts →</Link>
          </div>
          <div className="card-body" style={{ paddingTop: 0 }}>
            <table className="table">
              <thead>
                <tr>
                  <th>Severity</th>
                  <th>Target</th>
                  <th>What is wrong</th>
                  <th>Since</th>
                </tr>
              </thead>
              <tbody>
                {firing.map((a) => (
                  <tr key={a.id}>
                    <td><StatusChip status={a.severity} /></td>
                    <td>
                      <Link to={`/targets/${encodeURIComponent(a.targetId)}`}>{a.targetName}</Link>
                    </td>
                    <td>{a.summary}</td>
                    <td className="muted">{timeAgo(a.firedAt ?? a.startedAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div>
        <h2 className="page-title" style={{ fontSize: 'var(--text-lg)' }}>Components</h2>
        <p className="page-subtitle">
          Grouped as they are deployed. Only critical components can drive the
          ecosystem badge to critical.
        </p>
        <TargetGrid targets={data?.targets ?? []} />
      </div>
    </div>
  )
}

function GlobalHero({ status }: { status: GlobalStatus }) {
  const headline: Record<string, string> = {
    OK: 'All systems operational',
    WARNING: 'Degraded',
    CRITICAL: 'Critical problem',
    UNKNOWN: 'Status unknown',
  }

  return (
    <div className="card">
      <div className="hero-status">
        <span className="hero-glyph" style={{ color: STATUS_FILL[status.status] }} aria-hidden="true">
          {STATUS_GLYPH[status.status]}
        </span>
        <div className="hero-text">
          <span className="hero-label">{headline[status.status]}</span>
          <span className="hero-sub">
            {status.status === 'OK'
              ? `${status.okCount} component${status.okCount === 1 ? '' : 's'} healthy`
              : `Since ${status.since ? new Date(status.since).toLocaleString() : 'unknown'}`}
            {' · health score '}
            <strong className="tabular">{status.score.toFixed(0)}</strong>/100
            {' · checked '}{timeAgo(status.generatedAt)}
          </span>
        </div>
        <div style={{ marginLeft: 'auto' }}>
          <StatusChip status={status.status} />
        </div>
      </div>
    </div>
  )
}

function TargetGrid({ targets }: { targets: TargetStatus[] }) {
  if (targets.length === 0) {
    return <Empty>No targets are configured yet. Add them to config/targets.yml.</Empty>
  }

  const groups = new Map<string, TargetStatus[]>()
  for (const t of targets) {
    const list = groups.get(t.group) ?? []
    list.push(t)
    groups.set(t.group, list)
  }

  return (
    <div className="stack">
      {[...groups.entries()].map(([group, members]) => (
        <div key={group}>
          <div className="stat-label" style={{ marginBottom: 'var(--space-2)' }}>{group}</div>
          <div className="grid grid-3">
            {members.map((t) => <TargetCard key={t.targetId} target={t} />)}
          </div>
        </div>
      ))}
    </div>
  )
}

function TargetCard({ target }: { target: TargetStatus }) {
  return (
    <Link
      to={`/targets/${encodeURIComponent(target.targetId)}`}
      className="card"
      style={{
        display: 'block',
        padding: 'var(--space-4)',
        color: 'inherit',
        textDecoration: 'none',
        // A left rule in the status colour, alongside the chip, so the card's
        // state is legible from across a room without relying on hue alone.
        borderLeft: `4px solid ${STATUS_FILL[target.status]}`,
      }}
    >
      <div className="spread" style={{ marginBottom: 'var(--space-2)' }}>
        <strong>{target.name}</strong>
        <StatusChip status={target.status} />
      </div>

      <div className="muted" style={{ fontSize: 'var(--text-xs)' }}>
        {target.criticality.toLowerCase()}
        {' · probed '}{timeAgo(target.lastProbeAt)}
        {target.reachable && target.lastDurationMs !== undefined
          ? ` · ${target.lastDurationMs} ms`
          : ''}
      </div>

      {!target.reachable && target.lastError && (
        <div style={{
          marginTop: 'var(--space-2)',
          fontSize: 'var(--text-xs)',
          color: 'var(--status-critical)',
        }}>
          {target.lastError}
        </div>
      )}

      {target.reasons.length > 0 && (
        <ul style={{
          margin: 'var(--space-2) 0 0',
          paddingLeft: '1.1em',
          fontSize: 'var(--text-xs)',
          color: 'var(--text-secondary)',
        }}>
          {target.reasons.slice(0, 3).map((r) => (
            <li key={r.ruleId + (r.label ?? '')}>{r.summary}</li>
          ))}
          {target.reasons.length > 3 && (
            <li className="muted">+{target.reasons.length - 3} more</li>
          )}
        </ul>
      )}

      {target.status === 'OK' && target.reasons.length === 0 && (
        <div className="muted" style={{ marginTop: 'var(--space-2)', fontSize: 'var(--text-xs)' }}>
          {STATUS_LABEL.OK} — no rules firing
        </div>
      )}
    </Link>
  )
}
